<template>
  <Viewport>
    <main class="f-cover overflow-hidden">
      <vue-announcer />
      <RouterView ref="routerView" />

      <Webinars v-if="!loading" />

      <vue-progress-bar />

      <Transition name="fade">
        <Loading v-if="loading" />
      </Transition>

      <IePrompt class="z-400" />
    </main>
  </Viewport>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Loading from '@/components/utility/Loading.vue'
import Viewport from '@/components/utility/Viewport.vue'
import Webinars from '@/components/Webinars.vue'
const IePrompt = () => import('@/components/utility/IePrompt.vue')

export default {
  name: 'App',

  components: {
    Loading,
    IePrompt,
    Viewport,
    Webinars
  },

  computed: {
    ...mapState(['touch', 'browser', 'mobile', 'loading', 'title']),
    ...mapGetters(['posts', 'post'])
  },

  watch: {
    $route: {
      immediate: false,
      handler(to, from) {
        this.$nextTick(() => {
          this.setTitle()
          this.setAriaCurrent()
          if (!this.touch) {
            this.handleAppFocus(to, from)
          }
        })
      }
    },

    $mq: {
      immediate: true,
      handler() {
        const isMobile =
          this.$mq === 'sm' || this.$mq === 'md' || this.$mq === 'lg'

        const isPhone = this.$mq === 'sm' || this.$mq === 'md'

        this.$store.commit('isMobile', isMobile)
        this.$store.commit('isPhone', isPhone)
      }
    }
  },

  created() {
    this.$store.commit('browser', this.$browser.meta.name)

    // The progress bar
    this.$Progress.start()

    this.$router.afterEach(() => {
      this.$Progress.finish()
    })

    document.addEventListener('touchstart', this.isTouch)
  },

  mounted() {
    this.setAriaCurrent()
  },

  methods: {
    isTouch() {
      this.$store.commit('isTouch')
      document.body.setAttribute('id', 'touch')
      document.removeEventListener('touchstart', this.isTouch)
    },

    setTitle() {
      const title = this.post
        ? `${this.title} | ${this.post.title}`
        : this.title

      document.title = this.htmlDecode(title)

      // Announce the new page title to screen readers
      this.$announcer.set(this.htmlDecode(title))
    },

    setAriaCurrent() {
      this.$nextTick(() => {
        // Make sure any router-links of the current page have aria-current="page"
        const app = this.$el
        const currents = app.querySelectorAll('[aria-current]')

        if (currents) {
          currents.forEach(current => {
            current.removeAttribute('aria-current')
          })
        }

        app.querySelectorAll('.router-link-exact-active').forEach(current => {
          current.setAttribute('aria-current', 'page')
        })
      })
    },

    handleAppFocus(to, from) {
      // Attempt at blanket app focus managent
      // Has to be in a timeout as transitions mess with getting the elements on time
      setTimeout(() => {
        // Find any component el with the id focus-target - Obviously must
        // only put the id in els not rendered at the same time
        const componentFocusTarget = document.getElementById('focus-target')
        // Fallback focus target in a child router view
        const routeFocusTarget = this.$refs.routerView.$refs.focusTarget
        // If none of the above existent, use router view container
        const focusTarget = componentFocusTarget
          ? componentFocusTarget
          : routeFocusTarget
          ? routeFocusTarget
          : this.$refs.routerView.$el

        // Make focustarget programmatically focussable
        focusTarget.setAttribute('tabindex', '-1')

        // Dont want to mess with focus when using the pano sidebar
        const noFocusMaybe =
          (to.name === 'campus-panorama' && from.name === 'campus-panorama') ||
          (to.name === 'guided-panorama' && from.name === 'guided-panorama')

        if (!noFocusMaybe) {
          focusTarget.focus()
        }
      }, 400)
    }
  }
}
</script>

<style>
/* Purge css */
/* bg-primary */
/* bg-secondary */
/* bg-tertiary */
/* bg-accent-0 */
/* bg-accent-1 */
/* bg-accent-2 */
/* bg-accent-3 */
/* bg-accent-4 */
/* bg-accent-5 */
/* bg-accent-6 */
/* bg-accent-7 */
/* bg-accent-8 */
/* bg-accent-9 */
/* bg-accent-10 */
/* bg-accent-11 */
/* border-primary */
/* border-secondary */
/* border-tertiary */
/* border-accent-0 */
/* border-accent-1 */
/* border-accent-2 */
/* border-accent-3 */
/* border-accent-4 */
/* border-accent-5 */
/* border-accent-6 */
/* border-accent-7 */
/* border-accent-8 */
/* border-accent-9 */
/* border-accent-10 */
/* border-accent-11 */
/* hover:border-primary */
/* focus:border-primary */
/* hover:border-secondary */
/* focus:border-secondary */
/* hover:border-tertiary */
/* focus:border-tertiary */
/* hover:border-accent-0 */
/* focus:border-accent-0 */
/* hover:border-accent-1 */
/* focus:border-accent-1 */
/* hover:border-accent-2 */
/* focus:border-accent-2 */
/* hover:border-accent-3 */
/* focus:border-accent-3 */
/* hover:border-accent-4 */
/* focus:border-accent-4 */
/* hover:border-accent-5 */
/* focus:border-accent-5 */
/* hover:border-accent-6 */
/* focus:border-accent-6 */
/* hover:border-accent-7 */
/* focus:border-accent-7 */
/* hover:border-accent-8 */
/* focus:border-accent-8 */
/* hover:border-accent-9 */
/* focus:border-accent-9 */
/* hover:border-accent-10 */
/* focus:border-accent-10 */
/* hover:border-accent-11 */
/* hover:border-accent-11 */
/* focus:border-accent-11 */
/* bg-transparent */
/* group-hover:opacity-100 group-focus:opacity-100 pointer-events-none group-focus:pointer-events-auto */
/* text-grey-2 */
</style>
